import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { 
  KeyRound, 
  Lock, 
  ArrowRight, 
  ArrowLeft,
  Eye, 
  EyeOff,
  Shield,
  Zap,
  CheckCircle,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    setIsVisible(true);
    
    // Check if token is present
    if (!token) {
      toast.error('Invalid Reset Link', {
        description: 'No reset token found. Please request a new password reset.',
        duration: 5000,
      });
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { text: 'Very Weak', color: 'text-red-400' };
      case 2:
        return { text: 'Weak', color: 'text-orange-400' };
      case 3:
        return { text: 'Fair', color: 'text-yellow-400' };
      case 4:
        return { text: 'Good', color: 'text-blue-400' };
      case 5:
        return { text: 'Strong', color: 'text-green-400' };
      default:
        return { text: '', color: '' };
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthInfo = getPasswordStrengthText(passwordStrength);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Password Mismatch', {
        description: 'Passwords do not match. Please try again.',
        duration: 4000,
      });
      setLoading(false);
      return;
    }

    // Validate password strength
    if (passwordStrength < 3) {
      toast.error('Weak Password', {
        description: 'Please choose a stronger password with at least 8 characters, including uppercase, lowercase, and numbers.',
        duration: 5000,
      });
      setLoading(false);
      return;
    }

    try {
      await axios.post('/auth/reset-password', {
        token: token,
        new_password: formData.password
      });
      
      toast.success('Password Reset Successful!', {
        description: 'Your password has been updated. You can now sign in with your new password.',
        duration: 5000,
      });
      
      // Redirect to login after success
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error resetting password';
      toast.error('Reset Error', {
        description: message,
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && passwordStrength >= 3;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-slate-800/30"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className={`w-full max-w-md transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          
          {/* Logo and Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-2xl shadow-blue-500/25 mb-6">
              <KeyRound className="h-12 w-12 text-white drop-shadow-lg" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                APT-AccessHub
              </h1>
              <p className="text-blue-200/80 text-lg">
                Set your new password
              </p>
            </div>
            
            {/* Feature highlights */}
            <div className="flex justify-center space-x-6 mt-6">
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Shield className="h-4 w-4" />
                <span className="text-sm">Secure</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Zap className="h-4 w-4" />
                <span className="text-sm">Fast</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Reliable</span>
              </div>
            </div>
          </div>

          {/* Reset Password Form */}
          <Card className="backdrop-blur-xl bg-white/10 border-white/20 shadow-2xl shadow-black/20">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-semibold text-white">
                Reset Password
              </CardTitle>
              <p className="text-blue-200/70 text-sm mt-2">
                Enter your new password below
              </p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* New Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/90 font-medium">
                    New Password
                  </Label>
                  <div className="relative group">
                    <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'password' ? 'text-blue-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 pr-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-blue-400 focus:ring-blue-400/20 transition-all duration-200"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors duration-200"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
                      focusedField === 'password' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-white/70 text-sm">Password Strength:</span>
                        <span className={`text-sm font-medium ${strengthInfo.color}`}>
                          {strengthInfo.text}
                        </span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            passwordStrength <= 1 ? 'bg-red-500' :
                            passwordStrength === 2 ? 'bg-orange-500' :
                            passwordStrength === 3 ? 'bg-yellow-500' :
                            passwordStrength === 4 ? 'bg-blue-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white/90 font-medium">
                    Confirm New Password
                  </Label>
                  <div className="relative group">
                    <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'confirmPassword' ? 'text-blue-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 pr-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-blue-400 focus:ring-blue-400/20 transition-all duration-200"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors duration-200"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
                      focusedField === 'confirmPassword' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                  
                  {/* Password Match Indicator */}
                  {formData.confirmPassword && (
                    <div className="flex items-center space-x-2">
                      {formData.password === formData.confirmPassword ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                      )}
                      <span className={`text-sm ${
                        formData.password === formData.confirmPassword ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formData.password === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!isFormValid || loading}
                  className={`w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform ${
                    !loading && isFormValid ? 'hover:scale-105 hover:shadow-xl hover:shadow-blue-500/25' : ''
                  } ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Resetting password...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Reset Password</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Sign In</span>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <div className="flex justify-center items-center space-x-4 text-blue-300/60 text-xs mb-4">
              <div className="flex items-center space-x-1">
                <Shield className="h-3 w-3" />
                <span>AES-256 Encryption</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>SSL Secure</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Zap className="h-3 w-3" />
                <span>99.9% Uptime</span>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="text-blue-300/50 text-xs">
              Powered by <span className="font-medium text-blue-300/70">Budget Digital Solutions LLC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;